import { useEffect, useMemo, useState } from "react"
import { Loader2 } from "lucide-react"
import { typedEnv } from "../../config/typedEnv"

const AUTO_REDIRECT_GUARD_KEY = "loginAutoRedirectAt"
const AUTO_REDIRECT_COOLDOWN_MS = 60_000

// Safari pinned tabs keep their original site when a cross-site auth redirect
// opens elsewhere. Guard every browser to one auto-redirect per tab cooldown so
// reloads cannot spawn sign-in tabs repeatedly.
const shouldAutoRedirect = () => {
  try {
    const lastAttempt = Number(sessionStorage.getItem(AUTO_REDIRECT_GUARD_KEY) || 0)
    if (Date.now() - lastAttempt < AUTO_REDIRECT_COOLDOWN_MS) return false
    sessionStorage.setItem(AUTO_REDIRECT_GUARD_KEY, String(Date.now()))
    return true
  } catch {
    return true
  }
}

export const LoginPage = () => {
  const redirectUrl = `${window.location.origin}/`
  const [autoRedirecting] = useState(shouldAutoRedirect)

  const signInHref = useMemo(() => {
    const signInUrl = new URL(typedEnv.VITE_CLERK_SIGN_IN_URL)
    signInUrl.searchParams.set("redirect_url", redirectUrl)
    return signInUrl.toString()
  }, [redirectUrl])

  const signUpHref = useMemo(() => {
    const signUpUrl = new URL(typedEnv.VITE_CLERK_SIGN_UP_URL)
    signUpUrl.searchParams.set("redirect_url", redirectUrl)
    return signUpUrl.toString()
  }, [redirectUrl])

  useEffect(() => {
    if (autoRedirecting) window.location.replace(signInHref)
  }, [autoRedirecting, signInHref])

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center gap-4 bg-muted px-6"
      data-testid="login-page"
    >
      {autoRedirecting ? (
        <>
          <Loader2
            className="h-8 w-8 animate-spin text-muted-foreground"
            data-testid="login-spinner"
          />
          <p className="text-sm text-muted-foreground">Redirecting to sign in...</p>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">Continue to sign in</p>
      )}
      <div className="flex items-center gap-4 text-sm">
        <a
          href={signInHref}
          className="text-primary underline underline-offset-4"
          data-testid="login-sign-in"
        >
          Sign in
        </a>
        <a
          href={signUpHref}
          className="text-primary underline underline-offset-4"
          data-testid="login-sign-up"
        >
          Sign up
        </a>
      </div>
    </div>
  )
}
